import React, { forwardRef, memo, useImperativeHandle, useMemo, useState } from 'react';
import { Collapse, Typography } from '@mui/material';
import { ExpandMore, Repeat, Sync } from '@mui/icons-material';
import dayjs from 'dayjs';
import clsx from 'clsx';
import {
  defaultEventCategorySet,
  DefaultEventCategoryId,
  RoutineActivityRecurring,
} from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

import { ChatSuggestedEvent } from '../../../data-access';
import { getSuggestedBlockKey } from '../../../data-access/helpers';
import { formatTimeFromDayjs, formatTimeFromHHMM, isHHMMFormat } from '../../../helpers';
import { useGeneralSettings } from '../../../hooks';
import { getDefaultCategoryEventIconSet, PendingIndicatorDot } from '../../common';
import { AssistantChatSuggestedBlockCTA } from '../AssistantChatSuggestedBlockCTA';
import { SuggestedBlockHandle, SuggestedBlockActionsVisibility } from '../SuggestedBlocks/types';
import SuggestedBlockActionsRow from '../SuggestedBlocks/SuggestedBlockActionsRow';
import { toRoutineActivityRecurring } from '../../Scheduler/common/recurrence.helpers';
import RoutineRecurringSelector from '../../Scheduler/common/RoutineRecurringSelector/RoutineRecurringSelector';
import { DescriptionBox } from '../DescriptionBox';
import { formatDurationLabel, getDisplayDateLabel } from './helpers';
import { getSuggestedBlockPalette } from './useSuggestedBlockPalette';
import './AssistantChatSuggestedBlocks.scss';

interface AssistantChatSuggestedRoutineBlockProps {
  block: ChatSuggestedEvent;
  loadingEventId?: string;
  processedIds: string[];
  onProcessedIdsChange: (ids: string[]) => void;
  onManageCalendar?: (e: ChatSuggestedEvent) => Promise<boolean>;
  /** 'screen' renders the grouped-card action row (Accept/Discard) instead
   * of the plain chat-feed CTA — used by SuggestedBlockRow/SuggestionGroupCard. */
  variant?: 'default' | 'screen';
  onDiscard?: (eventId: string) => void;
  actionsVisibility?: SuggestedBlockActionsVisibility;
  defaultExpanded?: boolean;
}

/**
 * Suggested routine card matching v4 Figma / RN AssistantChatSuggestedRoutineBlock
 * (e.g. "Morning Routine") — Header (sync icon+title, duration pill, chevron),
 * collapsible Body (divider, start–end time pills, recurring-days selector,
 * detail), and the shared Discard + Accept row.
 */
const AssistantChatSuggestedRoutineBlock = memo(
  forwardRef<SuggestedBlockHandle, AssistantChatSuggestedRoutineBlockProps>(
    (
      {
        block,
        loadingEventId,
        processedIds,
        onProcessedIdsChange,
        onManageCalendar = () => Promise.resolve(false),
        variant = 'default',
        onDiscard,
        actionsVisibility = 'visible',
        defaultExpanded = true,
      },
      ref
    ) => {
      const { t } = useTranslation();
      const {
        generalSettings: { timeFormat },
      } = useGeneralSettings();

      const isScreenVariant = variant === 'screen';
      const palette = useMemo(
        () => getSuggestedBlockPalette('routine', isScreenVariant),
        [isScreenVariant]
      );
      const blockContainerStyle = {
        '--block-card-bg': palette.cardBackground,
        '--block-chip-bg': palette.chipBackground,
        '--block-time-bg': palette.timePillBackground,
        '--block-secondary-text': palette.secondaryText,
      } as React.CSSProperties;

      // Screen variant starts collapsed — matches RN isExpanded/handleToggleExpanded.
      const [isExpanded, setIsExpanded] = useState(defaultExpanded);
      const [selectedRoutineRecurring, setSelectedRoutineRecurring] =
        useState<RoutineActivityRecurring>(() => toRoutineActivityRecurring(block.recurring));

      const blockKey = getSuggestedBlockKey(block);
      const isProcessed = processedIds.includes(blockKey);
      const isLoading = loadingEventId === blockKey;
      const isDeleteAction = block.action === 'deleteRoutine';
      // Pending = still awaiting a decision (the accept/discard row is showing,
      // or its accept is in flight) — drives the pulsing dot on the header.
      const isPending = actionsVisibility === 'visible' || actionsVisibility === 'loading';

      const title = useMemo(() => {
        if (isProcessed) {
          return t('task.ai.suggestedBlocks.done');
        }
        if (isDeleteAction) {
          return t('task.ai.suggestedBlocks.deleteRoutine');
        }
        return block.before
          ? t('task.ai.suggestedBlocks.updateRoutine')
          : t('task.ai.suggestedBlocks.createRoutine');
      }, [block.before, isDeleteAction, isProcessed, t]);

      const category = block.categoryId
        ? defaultEventCategorySet.find((cat) => cat.id === block.categoryId)
        : null;

      const currentDateLabel = useMemo(
        () => getDisplayDateLabel(block.date, block.startTime),
        [block.date, block.startTime]
      );

      const durationLabel = useMemo(() => {
        if (block.durationMin) {
          return formatDurationLabel(block.durationMin);
        }
        const start = isHHMMFormat(block.startTime)
          ? dayjs(`2000-01-01T${block.startTime}`)
          : dayjs(block.startTime);
        const end = isHHMMFormat(block.endTime)
          ? dayjs(`2000-01-01T${block.endTime}`)
          : dayjs(block.endTime);
        if (!start.isValid() || !end.isValid()) {
          return null;
        }
        return formatDurationLabel(end.diff(start, 'minute'));
      }, [block.durationMin, block.startTime, block.endTime]);

      const handleAccept = async (): Promise<boolean> => {
        const payload = isDeleteAction ? block : { ...block, recurring: selectedRoutineRecurring };
        const result = await onManageCalendar(payload);
        if (result) {
          onProcessedIdsChange([...processedIds, getSuggestedBlockKey(block)]);
        }
        return result;
      };

      useImperativeHandle(
        ref,
        () => ({ accept: handleAccept }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [block, isDeleteAction, selectedRoutineRecurring]
      );

      const formatBlockTime = (time: string) =>
        isHHMMFormat(time)
          ? formatTimeFromHHMM(time, timeFormat, true)
          : formatTimeFromDayjs(dayjs(time), timeFormat, true);

      const bodyContent = (
        <>
          {!isDeleteAction && (
            <div className="assistant-chat-suggested-blocks__time-slots-row">
              <div className="assistant-chat-suggested-blocks__time-range-group">
                <div className="assistant-chat-suggested-blocks__time-slot-pill">
                  <Typography
                    variant="subtitle2"
                    className="assistant-chat-suggested-blocks__time-slot-text"
                  >
                    {formatBlockTime(block.startTime)}
                  </Typography>
                </div>
                <Typography
                  variant="subtitle2"
                  className="assistant-chat-suggested-blocks__time-slot-colon"
                >
                  :
                </Typography>
                <div className="assistant-chat-suggested-blocks__time-slot-pill">
                  <Typography
                    variant="subtitle2"
                    className="assistant-chat-suggested-blocks__time-slot-text"
                  >
                    {formatBlockTime(block.endTime)}
                  </Typography>
                </div>
              </div>
            </div>
          )}

          {!isDeleteAction && (
            <div className="assistant-chat-suggested-blocks__event-options">
              <RoutineRecurringSelector
                className="assistant-chat-suggested-blocks__routine-recurring-selector"
                recurring={selectedRoutineRecurring}
                selectedDate={dayjs(block.startTime)}
                hasSmallContainer
                disabled={isLoading || isProcessed}
                onDaySelect={(day) =>
                  setSelectedRoutineRecurring((prev) => ({
                    ...prev,
                    [day]: { enabled: !prev[day]?.enabled },
                  }))
                }
              />
            </div>
          )}

          {!!block.description && <DescriptionBox description={block.description} />}

          {isScreenVariant ? (
            <SuggestedBlockActionsRow
              visibility={isLoading ? 'loading' : actionsVisibility}
              onAccept={handleAccept}
              onDiscard={() => onDiscard?.(block.eventId)}
              acceptLabel={title}
              acceptDisabled={isProcessed}
            />
          ) : (
            <AssistantChatSuggestedBlockCTA
              title={title}
              loading={isLoading}
              type="calendar"
              onClick={handleAccept}
              disabled={isProcessed}
              processed={isProcessed}
            />
          )}
        </>
      );

      return (
        <div
          className={clsx('assistant-chat-suggested-blocks__block-container', {
            'assistant-chat-suggested-blocks__block-container--screen': isScreenVariant,
            'assistant-chat-suggested-blocks__block-container--routine': true,
          })}
          style={blockContainerStyle}
        >
          <div
            className={clsx('assistant-chat-suggested-blocks__block-row', {
              'assistant-chat-suggested-blocks__block-row--screen': isScreenVariant,
            })}
            onClick={isScreenVariant ? () => setIsExpanded((prev) => !prev) : undefined}
          >
            <div className="assistant-chat-suggested-blocks__before-wrapper assistant-chat-suggested-blocks__before-wrapper--flex">
              <div className="assistant-chat-suggested-blocks__title-with-category">
                {isScreenVariant ? (
                  <span className="assistant-chat-suggested-blocks__header-icon-wrapper">
                    <Sync fontSize="inherit" />
                  </span>
                ) : (
                  <span className="assistant-chat-suggested-blocks__category-item">
                    {category ? (
                      getDefaultCategoryEventIconSet(
                        category.id as DefaultEventCategoryId,
                        12,
                        'var(--color-black, #131314)'
                      )
                    ) : (
                      <Repeat fontSize="inherit" />
                    )}
                  </span>
                )}
                <Typography
                  variant="body2"
                  className="assistant-chat-suggested-blocks__title"
                  title={block.name}
                >
                  {block.name}
                </Typography>
              </div>
            </div>
            <div className="assistant-chat-suggested-blocks__datetime-wrapper">
              {isScreenVariant ? (
                <>
                  {!isDeleteAction && !!durationLabel && (
                    <div className="assistant-chat-suggested-blocks__simple-time-pill">
                      {!!currentDateLabel && currentDateLabel !== 'Today' && (
                        <Typography
                          variant="caption"
                          className="assistant-chat-suggested-blocks__simple-time-date"
                        >
                          {currentDateLabel}
                        </Typography>
                      )}
                      <Typography
                        variant="subtitle2"
                        className="assistant-chat-suggested-blocks__simple-time"
                      >
                        {durationLabel}
                      </Typography>
                    </div>
                  )}
                  <button
                    type="button"
                    className={clsx('assistant-chat-suggested-blocks__expand-toggle', {
                      'assistant-chat-suggested-blocks__expand-toggle--expanded': isExpanded,
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded((prev) => !prev);
                    }}
                    aria-label={
                      isExpanded
                        ? t('task.ai.suggestedBlocks.collapse')
                        : t('task.ai.suggestedBlocks.expand')
                    }
                  >
                    <ExpandMore fontSize="small" />
                  </button>
                </>
              ) : (
                <div
                  className={clsx(
                    'assistant-chat-suggested-blocks__time-wrapper',
                    'assistant-chat-suggested-blocks__time-item',
                    'assistant-chat-suggested-blocks__time-item--with-border'
                  )}
                >
                  <Typography variant="subtitle2" className="assistant-chat-suggested-blocks__time">
                    {durationLabel ?? `${block.durationMin ?? 0}m`}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <Collapse in={!isScreenVariant || isExpanded} timeout="auto" unmountOnExit>
            {bodyContent}
          </Collapse>
        </div>
      );
    }
  )
);

export default AssistantChatSuggestedRoutineBlock;
